import type { FilterDefinition, FilterHandler } from '../engine/types';
import { FilterId, FilterType, type RangeFilter } from '../types/filters';

const MIN_YEAR = 1950;
const MAX_YEAR = 2100;

export const graduationYearDefinition: FilterDefinition<RangeFilter> = {
  id: FilterId.GRADUATION_YEAR,
  name: 'Graduation Year',
  type: FilterType.RANGE,
  min: undefined,
  max: undefined,
  isActive: false,
  isVisible: true,
};

const clampYear = (
  year: number | undefined,
  min: number,
  max: number
): number | undefined => {
  if (year === undefined) {
    return undefined;
  }
  if (year < min) {
    return min;
  }
  if (year > max) {
    return max;
  }
  return year;
};

const isDefaultRange = (
  filterState: RangeFilter,
  definition: FilterDefinition<RangeFilter>
) => {
  const defaultMin = definition.min;
  const defaultMax = definition.max;

  return filterState.min === defaultMin && filterState.max === defaultMax;
};

export const graduationYearHandler: FilterHandler<RangeFilter> = {
  isActive(currentFilterState: RangeFilter) {
    const { min, max } = currentFilterState;
    return min !== undefined || max !== undefined;
  },
  isDefault(currentFilterState: RangeFilter, definition: RangeFilter) {
    return isDefaultRange(currentFilterState, definition);
  },
  getEffectiveFilter(
    currentFilterState: RangeFilter,
    definition: FilterDefinition<RangeFilter>
  ) {
    const isUserSubmitted = !isDefaultRange(currentFilterState, definition);

    if (!isUserSubmitted) {
      return {
        ...currentFilterState,
        min: undefined,
        max: undefined,
        isUserSubmitted: false,
        isVisible: false,
      };
    }

    const providedMin = currentFilterState.min;
    const providedMax = currentFilterState.max;

    const finalMin =
      providedMin !== undefined
        ? clampYear(providedMin, MIN_YEAR, MAX_YEAR)
        : undefined;
    let finalMax =
      providedMax !== undefined
        ? clampYear(providedMax, MIN_YEAR, MAX_YEAR)
        : undefined;

    if (
      finalMin !== undefined &&
      finalMax !== undefined &&
      finalMax < finalMin
    ) {
      finalMax = finalMin;
    }

    return {
      ...currentFilterState,
      min: finalMin,
      max: finalMax,
      isUserSubmitted: true,
      isVisible: true,
    };
  },
};
